import { logger } from '@heidi/runtime/logger';
import { create } from 'zustand';

import type {
  ToolStatus,
  TranscriptRole,
  VoiceDictationStopReason,
  VoiceEvidenceSource,
  VoiceSessionState,
} from '../../../contracts/voice-protocol';
import type { TimelineEntry } from '../../../contracts/voice-timeline.types';
import type { TranscriptEntry } from '../../../contracts/voice-transcript.types';
import type { VoiceSocketError } from '../../../data/voice/voiceSocket';
import { mapEvidenceToInfoCard } from '../ui/mapEvidenceToInfoCard';
import {
  findLiveDraft,
  findLiveDraftIndex,
  pushOrReviseDraft,
  toDraftDocument,
} from '../workspace/draftStack';
import type {
  InfoCard,
  WorkspaceComponent,
  WorkspaceCreateErrorCode,
  WorkspaceDraftDocument,
  WorkspaceDraftStatus,
  WorkspaceOpenInit,
  WorkspaceState,
  WorkspaceStep,
  WorkspaceStepStatus,
  WorkspaceThreadDraft,
} from '../workspace/workspace.types';

/**
 * What the store cannot resolve for itself. The workspace flag resolver reads
 * the OpenFeature client and a build-time env override, both of which this
 * root's package zone bans, so it stays app-side and its reader is injected.
 */
export interface VoiceAssistantStoreConfig {
  /**
   * Whether the voice workspace is enabled. A THUNK, called where the store
   * reads the flag rather than captured at creation, so a mid-session flip
   * lands exactly as the direct resolver call did.
   */
  isVoiceWorkspaceEnabled: () => boolean;
}

let injectedWorkspaceFlagReader:
  | VoiceAssistantStoreConfig['isVoiceWorkspaceEnabled']
  | null = null;
let reportedUnconfiguredRead = false;

/** Supplied by the app-side creation site (`src/utils/voice-assistant-store-wiring.ts`). */
export const configureVoiceAssistantStore = ({
  isVoiceWorkspaceEnabled,
}: VoiceAssistantStoreConfig): void => {
  injectedWorkspaceFlagReader = isVoiceWorkspaceEnabled;
};

/**
 * An unconfigured read is REPORTED, once, and never silently degrades.
 *
 * There is no honest default to fall back on: the resolver answers `true` under
 * the force-enable-all build override and whatever the flag says otherwise, so
 * any constant here is a guess that would read as an answer. The sibling data
 * seams throw on unconfigured use, and `@heidi/plugin-core` deliberately does
 * not — because it can recompute its input and a throw would turn a wiring
 * omission into a dead recorder. This one can do neither, and its wiring is
 * deferred by design (the eager chain is a main-bundle regression), so a throw
 * would turn a chunk that failed to load into a thrown error inside a store
 * setter. Reporting keeps the loss visible without taking the timeline with it.
 */
const readVoiceWorkspaceEnabled = (): boolean => {
  if (injectedWorkspaceFlagReader) return injectedWorkspaceFlagReader();
  if (!reportedUnconfiguredRead) {
    reportedUnconfiguredRead = true;
    logger.error(
      '[voice-assistant/store] workspace flag read before configureVoiceAssistantStore() — ' +
        'the app wiring (src/utils/voice-assistant-store-wiring.ts, imported by _app) is ' +
        'missing or has not resolved; answering false, which is NOT the resolver'
    );
  }
  return false;
};

export type VoiceConnectionStatus = 'idle' | 'connecting' | 'active' | 'error';

/**
 * The error line shown in the panel: display-ready text (server close
 * reasons, recorder messages) or a local failure code the panel localizes
 * at render time.
 */
export type VoiceAssistantError =
  | string
  | Extract<VoiceSocketError, { kind: 'not_signed_in' | 'voice_unavailable' }>;

// Declared in `@heidi/agents/contracts` since the history fetch that produces
// these entries moved into the package's `/data` zone, which may not import
// this module. Re-exported name-intact so every existing reader of
// `TranscriptEntry` keeps its specifier. `TimelineEntry` is there for the same
// reason and travels the same way — the dump formatter that renders it now
// lives behind the `@heidi/agents` door.
export type { TimelineEntry, TranscriptEntry };

export interface ToolEntry {
  id: string;
  /** Identity: registry / function name — never a display title. */
  name: string;
  status: ToolStatus;
  ts: number;
  /** Server-owned display string (always sent by current producers). */
  title?: string;
  /** Collapse / upsert key — required for running→terminal collapse. */
  callId?: string;
  /**
   * Background agent that produced this, when one did. Absent for inline
   * tools the realtime model ran itself. Lets activity be grouped per agent
   * rather than interleaved into one list.
   */
  agentId?: string;
}

export interface ApprovalState {
  tool: string;
  summary: string;
  /** Registry tool name when present (e.g. email_session_content). */
  toolName?: string;
  /** Recipients for email approvals — shown as an editable field. */
  toEmails?: string[];
  /**
   * Server-assigned id for this approval. Carried so a resolve can name which
   * approval it answers — today the store still holds one at a time, so
   * nothing depends on it yet.
   */
  approvalId?: string;
  /**
   * Which background agent is asking. With several agents running this is what
   * lets the clinician attribute a confirmation to what they asked for.
   */
  agentId?: string;
}

/**
 * Live state of a dictation capture. `committed` holds the settled segments;
 * `interim` holds Deepgram's volatile guess, which the next final replaces.
 * Rendering them separately is what keeps the panel from flickering words in
 * and out as the model revises them.
 */
export interface DictationState {
  active: boolean;
  committed: string;
  interim: string;
  lastStopReason: VoiceDictationStopReason | null;
}

interface VoiceAssistantState {
  isOpen: boolean;
  connection: VoiceConnectionStatus;
  sessionState: VoiceSessionState;
  muted: boolean;
  assistantSpeaking: boolean;
  greetingComplete: boolean;
  transcript: TranscriptEntry[];
  tools: ToolEntry[];
  /** Chronological message + tool + UI + approval events for the panel + dump. */
  timeline: TimelineEntry[];
  /**
   * Every approval awaiting the clinician, oldest first. The server can have
   * more than one outstanding once several background agents run.
   */
  approvals: ApprovalState[];
  /**
   * Head of {@link approvals}. Kept so existing consumers read one approval
   * unchanged while the queue exists underneath them.
   */
  approval: ApprovalState | null;
  dictation: DictationState;
  error: VoiceAssistantError | null;
  /**
   * Set when microphone capture is blocked by a denied permission (distinct
   * from a generic `error`). Drives the platform-aware recovery state instead
   * of the generic error line. Cleared on `reset` / new session.
   */
  micPermissionDenied: boolean;
  micStream: MediaStream | null;

  /**
   * The agent-driven workspace overlay. `null` when closed. When set, the
   * overlay renders (or, when `minimized`, collapses to a pill so the app
   * underneath is visible). See `../workspace`.
   */
  workspace: WorkspaceState | null;
  /**
   * Presented info cards, oldest first — the scrollable history feed the
   * `info-board` component renders. Lives outside the component so the feed
   * survives the canvas switching to a draft and back. Cleared with the
   * workspace / session, capped at {@link MAX_INFO_CARDS}.
   */
  infoCards: InfoCard[];
  /**
   * A live (un-created) draft that an info card displaced from the canvas.
   * Rendered as a "back to draft" affordance on the board; cleared when a
   * new draft opens or the workspace closes. Unused when drafts paint inline.
   */
  stashedDraft: WorkspaceDraftDocument | null;
  /**
   * Inline draft version stack in the speak thread (oldest first). Live =
   * newest non-superseded, non-created entry; `workspace_update` mutates it.
   */
  drafts: WorkspaceThreadDraft[];
  /** Which inline draft card is expanded; null collapses all. */
  expandedDraftId: string | null;

  open: () => void;
  /**
   * MINIMISE. Hide the surface but keep the live realtime session, mic, and
   * the full transcript/timeline alive so reopening resumes exactly where it
   * left off. Never resets session state — only {@link close} (END) does.
   * See AGE-1615 (history preserved across minimise) / AGE-1622.
   */
  minimize: () => void;
  /**
   * END the session (store half). Hide the surface AND wipe all session-scoped
   * state — transcript, timeline, tools, workspace, cards. Tearing down the
   * realtime session + mic is the controller's job (`voiceController.stop()`);
   * callers pair the two. Distinct from {@link minimize}, which keeps the
   * session alive. This is the only in-surface path that clears the transcript.
   */
  close: () => void;
  reset: () => void;
  /**
   * Rehydrate history from the server (AGE-1615). On open, pull the current
   * user's latest persisted voice transcript so history survives a full
   * close/reopen or reload — not just a minimise. Best-effort: degrades
   * silently when the endpoint is absent/errors (see `fetchLatestVoiceHistory`)
   * and only seeds when there is no local history, so it can never clobber a
   * live or just-minimised session's transcript.
   */
  hydrateFromServer: () => Promise<void>;
  setConnection: (status: VoiceConnectionStatus) => void;
  setSessionState: (state: VoiceSessionState) => void;
  setMuted: (muted: boolean) => void;
  setAssistantSpeaking: (speaking: boolean) => void;
  setGreetingComplete: (complete: boolean) => void;
  appendTranscript: (role: TranscriptRole, text: string) => void;
  /** Merge Evidence keypoints / sources onto one timeline card by callId. */
  upsertEvidence: (
    callId: string,
    patch: { text?: string; sources?: VoiceEvidenceSource[] }
  ) => void;
  upsertTool: (
    name: string,
    status: ToolStatus,
    callId?: string,
    title?: string,
    agentId?: string
  ) => void;
  appendUiEvent: (action: string, detail: string) => void;
  appendApprovalEvent: (
    phase: 'request' | 'resolved',
    tool: string,
    summary: string
  ) => void;
  setApproval: (approval: ApprovalState | null) => void;
  /** Queue an approval behind any already waiting. */
  addApproval: (approval: ApprovalState) => void;
  /**
   * Drop the approval with this id. Without one, drops the head — which is
   * what an untargeted server `approval_resolved` means.
   */
  removeApproval: (approvalId?: string) => void;
  startDictation: () => void;
  appendDictation: (text: string, final: boolean) => void;
  stopDictation: (text: string, reason: VoiceDictationStopReason) => void;
  setError: (message: VoiceAssistantError | null) => void;
  /** Flag / clear the mic-permission-denied recovery state. */
  setMicPermissionDenied: (denied: boolean) => void;
  setMicStream: (stream: MediaStream | null) => void;

  // --- Workspace overlay ---
  /** Open the workspace. Omit `init` (or its `component`) to open the idle shell. */
  openWorkspace: (init?: WorkspaceOpenInit) => void;
  /** Fill an open workspace with (or replace) its rendered component. */
  setWorkspaceComponent: (component: WorkspaceComponent) => void;
  minimizeWorkspace: () => void;
  restoreWorkspace: () => void;
  closeWorkspace: () => void;
  appendWorkspaceContent: (chunk: string) => void;
  setWorkspaceContent: (content: string) => void;
  setWorkspaceStatus: (status: WorkspaceDraftStatus) => void;
  setWorkspaceSteps: (steps: WorkspaceStep[]) => void;
  updateWorkspaceStep: (stepId: string, status: WorkspaceStepStatus) => void;
  /** Commit error surfaced next to the Create action; `null` clears it. */
  setWorkspaceCreateError: (error: WorkspaceCreateErrorCode | null) => void;
  /**
   * Append a presented card to the feed and bring the board to the front
   * (opening / restoring the workspace as needed). A live draft on the
   * canvas is stashed, not lost — see {@link restoreStashedDraft}.
   */
  presentInfoCard: (card: InfoCard) => void;
  /** Swap the canvas back to the draft an info card displaced. */
  restoreStashedDraft: () => void;
  setExpandedDraftId: (id: string | null) => void;
  /** Remove one thread draft (Discard). */
  discardDraft: (id: string) => void;
}

let idSeq = 0;
const nextId = () => `${Date.now()}-${idSeq++}`;

/** Feed history cap — old cards drop off the front once exceeded. */
const MAX_INFO_CARDS = 50;

type VoiceStoreSlice = {
  workspace: WorkspaceState | null;
  infoCards: InfoCard[];
  stashedDraft: WorkspaceDraftDocument | null;
  drafts: WorkspaceThreadDraft[];
  expandedDraftId: string | null;
};

/** Apply a patch to the live thread draft and mirror onto `workspace.component`. */
const withLiveDraftPatch = (
  state: VoiceStoreSlice,
  patch: (draft: WorkspaceThreadDraft) => WorkspaceThreadDraft
): Partial<VoiceStoreSlice> | null => {
  const index = findLiveDraftIndex(state.drafts);
  if (index < 0) return null;
  const nextDrafts = state.drafts.map((draft, i) => (i === index ? patch(draft) : draft));
  const patched = nextDrafts[index]!;
  if (!state.workspace) {
    return { drafts: nextDrafts };
  }
  const component =
    state.workspace.component?.kind === 'draft-document'
      ? toDraftDocument(patched)
      : state.workspace.component;
  return {
    drafts: nextDrafts,
    workspace: { ...state.workspace, component },
  };
};

/**
 * Open / restore the info-board with `card`. Same `card.id` replaces in place
 * (Evidence streams patches on one callId); new ids append.
 */
const withPresentedInfoCard = (
  state: VoiceStoreSlice,
  card: InfoCard
): VoiceStoreSlice => {
  const existingIdx = state.infoCards.findIndex((c) => c.id === card.id);
  const infoCards =
    existingIdx >= 0
      ? state.infoCards.map((c, i) => (i === existingIdx ? { ...card, ts: c.ts } : c))
      : [...state.infoCards, card].slice(-MAX_INFO_CARDS);
  const board = { kind: 'info-board' } as const;
  if (!state.workspace) {
    return {
      infoCards,
      stashedDraft: state.stashedDraft,
      drafts: state.drafts,
      expandedDraftId: state.expandedDraftId,
      workspace: { id: nextId(), minimized: false, component: board },
    };
  }
  // Prefer the thread live draft when stashing — canvas may already be the board.
  const live = findLiveDraft(state.drafts);
  const current = state.workspace.component;
  const stashedDraft =
    live && live.status !== 'created'
      ? toDraftDocument(live)
      : current?.kind === 'draft-document' && current.status !== 'created'
        ? current
        : state.stashedDraft;
  return {
    infoCards,
    stashedDraft,
    drafts: state.drafts,
    expandedDraftId: state.expandedDraftId,
    workspace: { ...state.workspace, minimized: false, component: board },
  };
};

const INITIAL = {
  connection: 'idle' as VoiceConnectionStatus,
  sessionState: 'connecting' as VoiceSessionState,
  muted: false,
  assistantSpeaking: false,
  greetingComplete: false,
  transcript: [] as TranscriptEntry[],
  tools: [] as ToolEntry[],
  timeline: [] as TimelineEntry[],
  approvals: [] as ApprovalState[],
  approval: null as ApprovalState | null,
  dictation: {
    active: false,
    committed: '',
    interim: '',
    lastStopReason: null,
  } as DictationState,
  error: null as VoiceAssistantError | null,
  micPermissionDenied: false,
  micStream: null as MediaStream | null,
  workspace: null as WorkspaceState | null,
  infoCards: [] as InfoCard[],
  stashedDraft: null as WorkspaceDraftDocument | null,
  drafts: [] as WorkspaceThreadDraft[],
  expandedDraftId: null as string | null,
};

/** Collapse key is callId only — no name fallback (plan L2 retired). */
function findRunningToolIndex(tools: ToolEntry[], callId?: string): number {
  if (!callId) return -1;
  for (let i = tools.length - 1; i >= 0; i -= 1) {
    const tool = tools[i];
    if (!tool || tool.status !== 'running') continue;
    if (tool.callId === callId) return i;
  }
  return -1;
}

export const useVoiceAssistantStore = create<VoiceAssistantState>((set, get) => ({
  isOpen: false,
  ...INITIAL,

  open: () => set({ isOpen: true }),
  // MINIMISE: hide the surface only. Session, mic, transcript, timeline, and
  // workspace all stay alive so reopening resumes in place (AGE-1615).
  minimize: () => set({ isOpen: false }),
  // END: hide the surface AND reset every session-scoped field (transcript,
  // timeline, tools, workspace overlay, card feed, approvals, dictation, and
  // the mic-permission-denied flag — all covered by ...INITIAL). The
  // realtime/mic teardown is the controller's half (`voiceController.stop()`).
  close: () => set({ isOpen: false, ...INITIAL }),
  reset: () => set({ ...INITIAL }),

  hydrateFromServer: async () => {
    // Never overwrite history we already hold: a live or just-minimised
    // session's in-memory transcript always wins over the server snapshot.
    if (get().transcript.length > 0) return;
    // Lazy import keeps the store free of the heavy HTTP stack at module load
    // — only the first rehydration pulls it in.
    const { fetchLatestVoiceHistory } =
      await import('../../../data/voice/voiceTranscriptApi');
    const entries = await fetchLatestVoiceHistory();
    if (!entries?.length) return;
    set((state) => {
      // Re-check under the setter: a turn may have landed while we awaited.
      if (state.transcript.length > 0) return state;
      const seededTimeline: TimelineEntry[] = entries.map((entry) => ({
        id: entry.id,
        ts: entry.ts,
        kind: 'transcript',
        role: entry.role,
        text: entry.text,
      }));
      return {
        transcript: entries,
        timeline: [...seededTimeline, ...state.timeline],
      };
    });
  },

  setConnection: (connection) => set({ connection }),
  setSessionState: (sessionState) => set({ sessionState }),
  setMuted: (muted) => set({ muted }),
  setAssistantSpeaking: (assistantSpeaking) => set({ assistantSpeaking }),
  setGreetingComplete: (greetingComplete) => set({ greetingComplete }),

  appendTranscript: (role, text) =>
    set((state) => {
      const trimmed = text.trim();
      if (!trimmed) return state;
      const id = nextId();
      const ts = Date.now();
      return {
        transcript: [
          ...state.transcript,
          {
            id,
            role,
            text: trimmed,
            ts,
          },
        ],
        timeline: [
          ...state.timeline,
          {
            id,
            ts,
            kind: 'transcript',
            role,
            text: trimmed,
          },
        ],
      };
    }),

  upsertEvidence: (callId, patch) =>
    set((state) => {
      const trimmedCallId = callId.trim();
      if (!trimmedCallId) return state;
      const sourceList =
        patch.sources && patch.sources.length > 0
          ? patch.sources.filter((s) => s.title.trim() || s.url?.trim())
          : undefined;
      const text = patch.text?.trim();
      if (!text && !sourceList?.length) return state;

      const idx = state.timeline.findIndex(
        (entry) => entry.kind === 'evidence' && entry.callId === trimmedCallId
      );
      let next: TimelineEntry;
      let timeline: TimelineEntry[];
      if (idx !== -1) {
        const existing = state.timeline[idx];
        if (existing?.kind !== 'evidence') return state;
        next = {
          ...existing,
          ...(text ? { text } : {}),
          ...(sourceList?.length ? { sources: sourceList } : {}),
        };
        timeline = state.timeline.slice();
        timeline[idx] = next;
      } else {
        const id = nextId();
        const ts = Date.now();
        next = {
          id,
          ts,
          kind: 'evidence',
          callId: trimmedCallId,
          text: text ?? '',
          ...(sourceList?.length ? { sources: sourceList } : {}),
        };
        timeline = [...state.timeline, next];
      }

      if (next.kind !== 'evidence' || !readVoiceWorkspaceEnabled()) {
        return { timeline };
      }
      const card = mapEvidenceToInfoCard({
        callId: next.callId,
        text: next.text,
        sources: next.sources,
        ts: next.ts,
      });
      if (!card) return { timeline };
      return { timeline, ...withPresentedInfoCard(state, card) };
    }),

  upsertTool: (name, status, callId, title, agentId) =>
    set((state) => {
      // Collapse running -> terminal for the same call (callId required).
      const idx = findRunningToolIndex(state.tools, callId);
      if (idx !== -1 && status !== 'running') {
        const tools = state.tools.slice();
        const current = tools[idx];
        if (!current) return state;
        const nextTitle = title || current.title;
        const ts = Date.now();
        tools[idx] = {
          ...current,
          name,
          status,
          ts,
          callId: current.callId ?? callId,
          ...(nextTitle ? { title: nextTitle } : {}),
        };

        const timeline = state.timeline.map((entry) =>
          entry.kind === 'tool' && entry.id === current.id
            ? {
                ...entry,
                name,
                status,
                ts,
                ...(nextTitle ? { title: nextTitle } : {}),
              }
            : entry
        );
        return { tools, timeline };
      }
      const id = nextId();
      const ts = Date.now();
      return {
        tools: [
          ...state.tools,
          {
            id,
            name,
            status,
            ts,
            ...(callId ? { callId } : {}),
            ...(title ? { title } : {}),
            ...(agentId ? { agentId } : {}),
          },
        ],
        timeline: [
          ...state.timeline,
          {
            id,
            ts,
            kind: 'tool',
            name,
            status,
            ...(title ? { title } : {}),
            ...(agentId ? { agentId } : {}),
          },
        ],
      };
    }),

  appendUiEvent: (action, detail) =>
    set((state) => ({
      timeline: [
        ...state.timeline,
        { id: nextId(), ts: Date.now(), kind: 'ui', action, detail },
      ],
    })),

  appendApprovalEvent: (phase, tool, summary) =>
    set((state) => ({
      timeline: [
        ...state.timeline,
        {
          id: nextId(),
          ts: Date.now(),
          kind: 'approval',
          tool,
          summary,
          phase,
        },
      ],
    })),

  setApproval: (approval) => set({ approvals: approval ? [approval] : [], approval }),

  addApproval: (approval) =>
    set((state) => {
      // Ignore a repeat of one already queued: the relay replays from the
      // start of the stream on re-attach, so the same approval can arrive
      // twice and must not produce two cards for one decision.
      //
      // Without an id, fall back to matching on what the card actually says.
      // Two indistinguishable cards are worse than one: each gates the same
      // clinical write, so confirming both performs it twice.
      const isDuplicate = approval.approvalId
        ? state.approvals.some((a) => a.approvalId === approval.approvalId)
        : state.approvals.some(
            (a) =>
              !a.approvalId &&
              a.tool === approval.tool &&
              a.summary === approval.summary &&
              a.agentId === approval.agentId
          );
      if (isDuplicate) {
        return state;
      }
      const approvals = [...state.approvals, approval];
      return { approvals, approval: approvals[0] ?? null };
    }),

  removeApproval: (approvalId) =>
    set((state) => {
      const approvals = approvalId
        ? state.approvals.filter((a) => a.approvalId !== approvalId)
        : state.approvals.slice(1);
      return { approvals, approval: approvals[0] ?? null };
    }),

  startDictation: () =>
    set({
      // Drive sessionState here too: the capture bar keys off dictation.active
      // while the status label keys off sessionState, and a missing/late
      // `state:dictating` frame must not leave those two disagreeing.
      sessionState: 'dictating',
      dictation: { active: true, committed: '', interim: '', lastStopReason: null },
    }),

  appendDictation: (text, final) =>
    set((state) => {
      const trimmed = text.trim();
      if (!trimmed) return state;
      if (!final) {
        return { dictation: { ...state.dictation, interim: trimmed } };
      }
      const committed = state.dictation.committed
        ? `${state.dictation.committed} ${trimmed}`
        : trimmed;
      // A final supersedes the interim it was revising — keeping both would
      // show the same words twice.
      return { dictation: { ...state.dictation, committed, interim: '' } };
    }),

  stopDictation: (text, reason) =>
    set((state) => ({
      sessionState: state.sessionState === 'dictating' ? 'thinking' : state.sessionState,
      dictation: {
        active: false,
        // Trust the server's full-capture text over locally accumulated
        // segments: a dropped frame would otherwise leave a gap in the note.
        committed: text || state.dictation.committed,
        interim: '',
        lastStopReason: reason,
      },
    })),

  setError: (error) => set({ error, connection: error ? 'error' : INITIAL.connection }),
  setMicPermissionDenied: (micPermissionDenied) =>
    set({
      micPermissionDenied,
      connection: micPermissionDenied ? 'error' : INITIAL.connection,
    }),
  setMicStream: (micStream) => set({ micStream }),

  // --- Workspace overlay ---
  openWorkspace: (init) =>
    set((state) => {
      const component = init?.component ?? null;
      if (component?.kind === 'draft-document') {
        const { drafts, id } = pushOrReviseDraft(state.drafts, component, nextId);
        const live = findLiveDraft(drafts)!;
        return {
          workspace: {
            id: init?.id ?? state.workspace?.id ?? nextId(),
            minimized: false,
            component: toDraftDocument(live),
          },
          drafts,
          expandedDraftId: id,
          stashedDraft: null,
        };
      }
      return {
        workspace: {
          id: init?.id ?? nextId(),
          minimized: false,
          component,
        },
        stashedDraft: null,
      };
    }),

  setWorkspaceComponent: (component) =>
    set((state) => {
      if (!state.workspace) return state;
      if (component.kind === 'draft-document') {
        const { drafts, id } = pushOrReviseDraft(state.drafts, component, nextId);
        const live = findLiveDraft(drafts)!;
        return {
          workspace: {
            ...state.workspace,
            component: toDraftDocument(live),
          },
          drafts,
          expandedDraftId: id,
          stashedDraft: null,
        };
      }
      return { workspace: { ...state.workspace, component } };
    }),

  minimizeWorkspace: () =>
    set((state) =>
      state.workspace ? { workspace: { ...state.workspace, minimized: true } } : state
    ),

  restoreWorkspace: () =>
    set((state) =>
      state.workspace ? { workspace: { ...state.workspace, minimized: false } } : state
    ),

  closeWorkspace: () =>
    set({
      workspace: null,
      infoCards: [],
      stashedDraft: null,
      drafts: [],
      expandedDraftId: null,
    }),

  appendWorkspaceContent: (chunk) =>
    set((state) => {
      const patched = withLiveDraftPatch(state, (draft) => ({
        ...draft,
        content: draft.content + chunk,
      }));
      return patched ?? state;
    }),

  setWorkspaceContent: (content) =>
    set((state) => {
      const patched = withLiveDraftPatch(state, (draft) => ({ ...draft, content }));
      return patched ?? state;
    }),

  setWorkspaceStatus: (status) =>
    set((state) => {
      const patched = withLiveDraftPatch(state, (draft) => ({ ...draft, status }));
      return patched ?? state;
    }),

  setWorkspaceSteps: (steps) =>
    set((state) => {
      const patched = withLiveDraftPatch(state, (draft) => ({ ...draft, steps }));
      return patched ?? state;
    }),

  updateWorkspaceStep: (stepId, status) =>
    set((state) => {
      const patched = withLiveDraftPatch(state, (draft) => ({
        ...draft,
        steps: draft.steps.map((step) =>
          step.id === stepId ? { ...step, status } : step
        ),
      }));
      return patched ?? state;
    }),

  setWorkspaceCreateError: (error) =>
    set((state) => {
      const patched = withLiveDraftPatch(state, (draft) => {
        const next = { ...draft };
        if (error) next.createError = error;
        else delete next.createError;
        return next;
      });
      return patched ?? state;
    }),

  presentInfoCard: (card) => set((state) => withPresentedInfoCard(state, card)),

  restoreStashedDraft: () =>
    set((state) => {
      if (!state.workspace || !state.stashedDraft) return state;
      const live = findLiveDraft(state.drafts);
      return {
        workspace: {
          ...state.workspace,
          component: live ? toDraftDocument(live) : state.stashedDraft,
        },
        stashedDraft: null,
        expandedDraftId: live?.id ?? state.expandedDraftId,
      };
    }),

  setExpandedDraftId: (expandedDraftId) => set({ expandedDraftId }),

  discardDraft: (id) =>
    set((state) => {
      const discarded = state.drafts.find((draft) => draft.id === id);
      let drafts = state.drafts.filter((draft) => draft.id !== id);
      // Discarding the live revision should revive the prior superseded one
      // so Create doesn't disappear behind a stranded "Superseded" chip.
      if (discarded && !discarded.superseded && discarded.status !== 'created') {
        const priorId = discarded.replacesId;
        if (priorId) {
          drafts = drafts.map((draft) =>
            draft.id === priorId && draft.superseded
              ? { ...draft, superseded: false }
              : draft
          );
        }
      }
      const live = findLiveDraft(drafts);
      const expandedDraftId =
        state.expandedDraftId === id
          ? (live?.id ?? drafts[drafts.length - 1]?.id ?? null)
          : state.expandedDraftId;
      const workspace =
        state.workspace && live
          ? { ...state.workspace, component: toDraftDocument(live) }
          : state.workspace && state.workspace.component?.kind === 'draft-document'
            ? { ...state.workspace, component: null }
            : state.workspace;
      return { drafts, expandedDraftId, workspace };
    }),
}));
